import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
    notificationTemplateApi,
    type MessageDraftInput,
    type MessagePreview,
} from '@/entities/notification';
import {
    tenantApi,
    type CompanyDetails,
    type DomainType,
    type HouseStyle,
    type MailSettingsInput,
    type TenantSettings,
    type TenantSettingsPatch,
} from '@/entities/tenant';
import { reportApi, reportKeys } from '@/entities/report';
import { templateCostSourcesApi } from '@/entities/template';
import { teamApi, teamKeys, type TenantRole } from '@/entities/user';
import { useDebounced } from '@/shared/lib';
import { reloadWorkspaceSession } from '@/shared/ui';

/**
 * Alle serverstaat van scherm 11. De sleutels zijn hiërarchisch zodat één
 * mutatie precies de bijbehorende query kan verversen.
 */
export const settingsKeys = {
    tenant: ['tenant'] as const,
    branding: ['tenant', 'branding'] as const,
    settings: ['tenant', 'settings'] as const,
    mail: ['tenant', 'mail-settings'] as const,
    domains: ['tenant', 'domains'] as const,
    /* Dezelfde lijsten als de inspecteurskeuze: een wijziging hier ververst die ook. */
    members: teamKeys.members(),
    invitations: teamKeys.invitations(),
    costSources: ['catalog', 'cost-sources'] as const,
    clientMessages: ['notification-templates', 'customer'] as const,
};

export function useTenant() {
    return useQuery({ queryKey: settingsKeys.tenant, queryFn: () => tenantApi.current() });
}

export function useBranding() {
    return useQuery({ queryKey: settingsKeys.branding, queryFn: () => tenantApi.branding() });
}

export function useTenantSettings() {
    return useQuery({ queryKey: settingsKeys.settings, queryFn: () => tenantApi.settings() });
}

/**
 * Wachten er verstuurde rapporten op hun PDF, en ontbreekt de PDF-dienst? Een
 * foutmelding hier laat het blok gewoon weg: het is een melding, geen instelling.
 */
export function usePdfBacklog() {
    return useQuery({
        queryKey: reportKeys.pdfService,
        queryFn: () => reportApi.pdfService(),
        retry: false,
        staleTime: 60_000,
    });
}

/** Alleen voor de eigenaar: de API weigert anderen, dus het scherm vraagt het niet. */
export function useMailSettings(enabled: boolean) {
    return useQuery({
        queryKey: settingsKeys.mail,
        queryFn: () => tenantApi.mailSettings(),
        enabled,
    });
}

/** Alleen voor wie instellingen beheert: de API weigert anderen, dus het scherm vraagt het niet. */
export function useTemplateCostSources(enabled: boolean) {
    return useQuery({
        queryKey: settingsKeys.costSources,
        queryFn: () => templateCostSourcesApi.list(),
        enabled,
    });
}

/** Zo vaak kijkt het scherm mee zolang het platform een domein klaarzet. */
export const DOMAIN_SETUP_POLL_MS = 20_000;

/**
 * De eigen domeinen. Zolang het platform er een klaarzet (op de server, het certificaat, de
 * controle), ververst de lijst vanzelf: het bedrijf ziet de stappen afgaan zonder te herladen.
 */
export function useDomains() {
    return useQuery({
        queryKey: settingsKeys.domains,
        queryFn: () => tenantApi.domains(),
        refetchInterval: (query) =>
            query.state.data?.some((domain) => domain.setup.inProgress)
                ? DOMAIN_SETUP_POLL_MS
                : false,
    });
}

/**
 * Het team: de mensen met een lidmaatschap van dit bedrijf. Welke andere
 * bedrijven iemand nog meer heeft is hier onzichtbaar - dat is bedrijfsgevoelig
 * en hoort alleen in het platformbeheer.
 */
export function useTeamMembers(enabled = true) {
    return useQuery({
        queryKey: settingsKeys.members,
        queryFn: () => teamApi.members(),
        enabled,
    });
}

export function useInvitations(enabled = true) {
    return useQuery({
        queryKey: settingsKeys.invitations,
        queryFn: () => teamApi.invitations(),
        enabled,
    });
}

/**
 * De handelsnaam en het logo in de kopbalk en de rail komen uit de sessie. Na een
 * wijziging haalt de app die opnieuw op, zonder op dat antwoord te wachten; lukt het
 * nu niet, dan staan ze er bij het volgende opstarten.
 */
function refreshWorkspaceHeader(client: QueryClient): void {
    void reloadWorkspaceSession(client).catch(() => undefined);
}

/**
 * De bedrijfsgegevens opslaan; het scherm toont daarna wat de server bewaarde, en de
 * kopbalk de handelsnaam.
 */
export function useUpdateCompanyDetails() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (details: CompanyDetails) => tenantApi.updateDetails(details),
        onSuccess: (tenant) => {
            client.setQueryData(settingsKeys.tenant, tenant);
            refreshWorkspaceHeader(client);
        },
    });
}

/**
 * De drie huisstijlkleuren in één keer. De twee naast het accent mogen `null`
 * zijn: de API kiest dan zelf wat leesbaar is, en koppen houden hun eigen inkt.
 */
export function useUpdateHouseStyle() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (style: HouseStyle) =>
            tenantApi.updateBranding({
                accentColor: style.accent,
                accentTextColor: style.accentText,
                headingColor: style.heading,
            }),
        onSuccess: (branding) => client.setQueryData(settingsKeys.branding, branding),
    });
}

/** Uploaden en kiezen in één; `null` haalt het logo weg, ook uit de kopbalk. */
export function useUpdateLogo() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (file: File | null) =>
            file ? tenantApi.uploadLogo(file) : tenantApi.updateBranding({ logoMediaUuid: null }),
        onSuccess: (branding) => {
            client.setQueryData(settingsKeys.branding, branding);
            void client.invalidateQueries({ queryKey: settingsKeys.tenant, exact: true });
            refreshWorkspaceHeader(client);
        },
    });
}

/**
 * Eén schakelaar stuurt precies één instelling. Hij springt meteen om
 * (optimistisch) en draait terug als de server het weigert - wachten op een
 * netwerkrondje voelt als een kapotte knop.
 */
export function useUpdateSettings() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (patch: TenantSettingsPatch) => tenantApi.updateSettings(patch),
        onMutate: async (patch) => {
            await client.cancelQueries({ queryKey: settingsKeys.settings });
            const previous = client.getQueryData<TenantSettings>(settingsKeys.settings);

            if (previous) {
                client.setQueryData<TenantSettings>(settingsKeys.settings, {
                    ...previous,
                    ...patch,
                });
            }

            return { previous };
        },
        onError: (_error, _patch, context) => {
            if (context?.previous) {
                client.setQueryData(settingsKeys.settings, context.previous);
            }
        },
        onSuccess: (settings) => client.setQueryData(settingsKeys.settings, settings),
    });
}

export function useUpdateMailSettings() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: MailSettingsInput) => tenantApi.updateMailSettings(input),
        onSuccess: (settings) => client.setQueryData(settingsKeys.mail, settings),
    });
}

/** Een geslaagde test zet `verified_at`: daarna de instellingen opnieuw lezen. */
export function useSendTestMail() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (recipient: string) => tenantApi.sendTestMail(recipient),
        onSettled: () => client.invalidateQueries({ queryKey: settingsKeys.mail }),
    });
}

export function useAddDomain() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { hostname: string; type: DomainType }) => tenantApi.addDomain(input),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.domains }),
    });
}

export function useVerifyDomain() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (domainUuid: string) => tenantApi.verifyDomain(domainUuid),
        onSettled: () => client.invalidateQueries({ queryKey: settingsKeys.domains }),
    });
}

/** Het klaarzetten opnieuw starten; de lijst laat daarna de stap zien waar het weer begint. */
export function useRetryDomainSetup() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (domainUuid: string) => tenantApi.retryDomainSetup(domainUuid),
        onSettled: () => client.invalidateQueries({ queryKey: settingsKeys.domains }),
    });
}

/** Een ander domein van dezelfde soort is daarna geen hoofddomein meer: de hele lijst opnieuw. */
export function useMakePrimaryDomain() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (domainUuid: string) => tenantApi.makePrimary(domainUuid),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.domains }),
    });
}

export function useRemoveDomain() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (domainUuid: string) => tenantApi.removeDomain(domainUuid),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.domains }),
    });
}

export function useInvite() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { email: string; role: TenantRole }) => teamApi.invite(input),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.invitations }),
    });
}

/** Een nieuwe link: de vorige werkt daarna niet meer. */
export function useResendInvitation() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (invitationUuid: string) => teamApi.resendInvitation(invitationUuid),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.invitations }),
    });
}

export function useWithdrawInvitation() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (invitationUuid: string) => teamApi.withdrawInvitation(invitationUuid),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.invitations }),
    });
}

export function useSetRoles() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { userUuid: string; roles: readonly TenantRole[] }) =>
            teamApi.setRoles(input.userUuid, input.roles),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.members }),
    });
}

/** Uitschakelen raakt alleen het lidmaatschap; het account blijft bestaan. */
export function useDisableMember() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (userUuid: string) => teamApi.disable(userUuid),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.members }),
    });
}

/** Weer toelaten: het lidmaatschap wordt weer actief, met de rollen van toen. */
export function useEnableMember() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (userUuid: string) => teamApi.enable(userUuid),
        onSuccess: () => client.invalidateQueries({ queryKey: settingsKeys.members }),
    });
}

/* -------------------------------------------------------------------------- */
/* Berichten aan klanten                                                       */
/* -------------------------------------------------------------------------- */

/** Alleen voor wie instellingen beheert: het scherm biedt de editor anderen niet aan. */
export function useClientMessages(enabled: boolean) {
    return useQuery({
        queryKey: settingsKeys.clientMessages,
        queryFn: () => notificationTemplateApi.clientMessages(),
        enabled,
    });
}

/** Een eigen tekst voor één kanaal; de lijst haalt daarna op wat nu uitgaat. */
export function useSaveClientMessage() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: MessageDraftInput) => notificationTemplateApi.save(input),
        onSettled: () => client.invalidateQueries({ queryKey: settingsKeys.clientMessages }),
    });
}

/** De eigen tekst weg: klanten krijgen weer de standaardtekst. */
export function useRestoreClientMessage() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (uuid: string) => notificationTemplateApi.restoreDefault(uuid),
        onSettled: () => client.invalidateQueries({ queryKey: settingsKeys.clientMessages }),
    });
}

export function useSendTestMessage() {
    return useMutation({
        mutationFn: (input: { key: string; subject: string; body: string }) =>
            notificationTemplateApi.sendTestMail(input),
    });
}

/** Hoe lang het voorbeeld wacht nadat je stopt met typen. */
export const PREVIEW_DELAY_MS = 500;

export interface MessagePreviewState {
    /** Het laatste voorbeeld dat lukte; blijft staan terwijl een nieuw onderweg is. */
    preview: MessagePreview | null;
    /** Waarom het laatste verzoek mislukte, tot een volgend verzoek lukt. */
    error: unknown;
    updating: boolean;
}

/**
 * Het voorbeeld van een concept, opnieuw zodra je even stopt met typen.
 *
 * Een voorbeeld is een POST en hoort niet in de querycache: die wordt bewaard op
 * het apparaat, en een verouderd voorbeeld van een half getypte tekst heeft
 * daar niets te zoeken. Van de verzoeken die tegelijk lopen telt alleen het
 * laatste (`mutate` roept zijn eigen callbacks alleen voor het laatste aan).
 * `null` vraagt niets op, bijvoorbeeld zolang de tekst een fout heeft; het
 * laatste goede voorbeeld blijft dan staan.
 */
export function useMessagePreview(
    draft: MessageDraftInput | null,
    delayMs = PREVIEW_DELAY_MS,
): MessagePreviewState {
    const [preview, setPreview] = useState<MessagePreview | null>(null);
    const [error, setError] = useState<unknown>(null);
    const { mutate, isPending } = useMutation({
        mutationFn: (input: MessageDraftInput) => notificationTemplateApi.preview(input),
    });

    const subject = useDebounced(draft?.subject ?? null, delayMs);
    const body = useDebounced(draft?.body ?? null, delayMs);
    const key = draft?.key ?? null;
    const channel = draft?.channel ?? null;

    useEffect(() => {
        if (key === null || channel === null || body === null) {
            return;
        }

        mutate(
            { key, channel, subject, body },
            {
                onSuccess: (result) => {
                    setPreview(result);
                    setError(null);
                },
                onError: (failure) => setError(failure),
            },
        );
    }, [key, channel, subject, body, mutate]);

    return { preview, error, updating: isPending };
}
